import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  LOCALE_ID,
  OnDestroy,
} from '@angular/core';
import { IUserDTO } from '../../../dto/user/user.dto';
import { DownloadService } from '../../../service/download.service';
import { Router } from '@angular/router';
import lightGallery from 'lightgallery';
import { LightGallery } from 'lightgallery/lightgallery';
import { defaultLightGallerySettings } from '../../../config/light-gallery';
import { IContentDTO } from '../../../dto/content/content.dto';
import { GalleryItem } from 'lightgallery/lg-utils';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzAvatarComponent } from 'ng-zorro-antd/avatar';
import { NzTypographyComponent } from 'ng-zorro-antd/typography';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { IconVerifiedComponent } from '../../../component/icon/verified/icon-verified.component';
import { NzStatisticComponent } from 'ng-zorro-antd/statistic';
import { DecimalPipe, DOCUMENT } from '@angular/common';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { AnalyticsService } from '../../../service/analytics.service';
import { ConfigService } from '../../../service/config.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-info',
  imports: [
    NzRowDirective,
    NzColDirective,
    NzAvatarComponent,
    NzTypographyComponent,
    NzIconDirective,
    NzTooltipDirective,
    IconVerifiedComponent,
    NzStatisticComponent,
    DecimalPipe,
    NzTagComponent,
  ],
  templateUrl: './info.component.html',
  styleUrl: './info.component.less',
})
export class InfoComponent implements OnDestroy {
  public readonly userDTO: InputSignal<IUserDTO> = input.required<IUserDTO>();
  private readonly router: Router = inject(Router);
  private readonly downloadService: DownloadService = inject(DownloadService);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly localeId: string = inject(LOCALE_ID);
  private lightGallery?: LightGallery = undefined;

  public ngOnDestroy(): void {
    this.closeLightGallery();
  }

  public hasAvatar(userDTO: IUserDTO): boolean {
    return 0 !== userDTO.pictures.length;
  }

  public hasAddress(userDTO: IUserDTO): boolean {
    if (!userDTO.address) {
      return false;
    }
    return !!(
      userDTO.address.postalCode?.length ||
      userDTO.address.city?.length ||
      userDTO.address.value?.length
    );
  }

  public getThumbnailUrl(userDTO: IUserDTO): string | undefined {
    if (!this.hasAvatar(userDTO)) {
      return undefined;
    }
    const fileUrl: string = userDTO.pictures[userDTO.pictures.length - 1].url;
    return this.downloadService.getDownloadUrl(fileUrl);
  }

  public getAddress(userDTO: IUserDTO): string | undefined {
    if (!this.hasAddress(userDTO)) {
      return undefined;
    }
    return [
      userDTO.address!.postalCode,
      userDTO.address!.city,
      userDTO.address!.value,
    ]
      .filter((it: string | undefined): boolean => !!it?.length)
      .join(', ');
  }

  public onAvatarClick(userDTO: IUserDTO): void {
    if (!this.hasAvatar(userDTO)) {
      return;
    }
    const contentDTO: IContentDTO = userDTO.pictures[0];
    const thumbnailContentDTO: IContentDTO =
      userDTO.pictures[userDTO.pictures.length - 1];
    const url: string = this.downloadService.getDownloadUrl(contentDTO.url);
    const thumbnailUrl: string = this.downloadService.getDownloadUrl(
      thumbnailContentDTO.url
    );
    const width: string = contentDTO.width.toString();
    const galleryItem: GalleryItem = {
      id: userDTO.primaryKey,
      src: url,
      thumb: thumbnailUrl,
      downloadUrl: url,
      width: width,
      alt: userDTO.username,
    };
    this.lightGallery = lightGallery(this.document.body, {
      ...defaultLightGallerySettings,
      dynamicEl: [galleryItem],
    });

    const username: string = this.userDTO().username;
    this.document.body.addEventListener(
      'lgAfterClose',
      (): void => {
        this.lightGallery!.destroy();
        this.lightGallery = undefined;
        this.analyticsService.event('gallery_exec', {
          action: 'close',
          content: 'avatar',
          user: username,
          path: this.router.url,
        });
      },
      { once: true }
    );

    this.document.body.addEventListener(
      'lgAfterOpen',
      (): void => {
        this.analyticsService.event('gallery_exec', {
          action: 'open',
          content: 'avatar',
          user: username,
          path: this.router.url,
        });
      },
      { once: true }
    );

    if (this.document.defaultView) {
      this.document.defaultView.addEventListener(
        'popstate',
        (): void => this.closeLightGallery(),
        { once: true }
      );
    }

    this.lightGallery.openGallery();
  }

  public onUserTagClick(username: string): void {
    this.router.navigate(['/profiles', username]).then((): void => {
      this.analyticsService.event('user_exec', {
        origin: 'info',
        username: username,
        path: this.router.url,
      });
    });
  }

  private closeLightGallery(): void {
    if (this.lightGallery && this.lightGallery.lgOpened) {
      this.lightGallery.closeGallery();
    }
  }
}
